

export const TASKS_TOOLS = [
  {
    name: 'get_boards',
    description:
      'List all Task Manager boards (id, name, task count). Call before create_task/update_task when you only have a board name, or to find a board/task the user refers to.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'get_board',
    description:
      'Get a full board by id or name: its lists (groups) and tasks (id, title, description text, priority, status, due date, group_id, done, subtasks). status is todo / in_progress / done / dismissed. Images in a description appear as "🖼 Image: name" lines. Use before update_task, before running a task (read its description), or to locate a task by title.',
    input_schema: {
      type: 'object',
      properties: {
        board_id: { type: 'string' },
        board_name: {
          type: 'string',
          description: 'Board name — use when the user names the board instead of an id',
        },
      },
      required: [],
    },
  },
  {
    name: 'create_board',
    description:
      'Create a Task Manager board, optionally with initial lists (columns). Always switches to the Task Manager panel and opens the new board. Returns board_id and the created list ids — use them for create_task in follow-up calls. When the user asks for a board with a plan/steps, create the board, then its lists, then tasks; do not stop after only creating the board.',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        description: { type: 'string' },
        color: { type: 'string', description: 'Optional hex color like #4f46e5' },
        lists: {
          type: 'array',
          description: 'Optional initial list/column names, e.g. ["To Do","Doing","Done"]',
          items: { type: 'string' },
        },
      },
      required: ['name'],
    },
  },
  {
    name: 'open_board',
    description:
      "Open an existing board in the Task Manager panel (switches to it). Use when the user says 'open', 'show', or 'go to' a board. Provide board_id or board_name.",
    input_schema: {
      type: 'object',
      properties: {
        board_id: { type: 'string' },
        board_name: { type: 'string' },
      },
      required: [],
    },
  },
  {
    name: 'rename_board',
    description:
      'Rename an existing board. Provide board_id (or board_name to locate it) and the new name.',
    input_schema: {
      type: 'object',
      properties: {
        board_id: { type: 'string' },
        board_name: { type: 'string' },
        name: { type: 'string' },
      },
      required: ['name'],
    },
  },
  {
    name: 'delete_board',
    description:
      'Delete a board permanently, including its lists and tasks. Provide board_id (or board_name). Destructive — only call when the user clearly asks to delete/remove a board.',
    input_schema: {
      type: 'object',
      properties: {
        board_id: { type: 'string' },
        board_name: { type: 'string' },
      },
      required: [],
    },
  },
  {
    name: 'create_list',
    description:
      'Add a list (column/group) to a board. Requires board_id. Returns the new list id for placing tasks.',
    input_schema: {
      type: 'object',
      properties: {
        board_id: { type: 'string' },
        name: { type: 'string' },
      },
      required: ['board_id', 'name'],
    },
  },
  {
    name: 'create_task',
    description:
      'Create a task (card) on a board. Requires board_id and title. Pass group_id to place it in a specific list (defaults to the first list). Pass subtasks for a checklist of steps inside the task. Use description for a short summary; use write_task_description for rich multi-paragraph content.',
    input_schema: {
      type: 'object',
      properties: {
        board_id: { type: 'string' },
        group_id: { type: 'string', description: 'Target list id; defaults to first list' },
        title: { type: 'string' },
        description: { type: 'string', description: 'Optional plain-text/markdown summary' },
        format: { type: 'string', enum: ['markdown', 'html'], description: 'Format of description (default markdown)' },
        priority: { type: 'string', enum: ['low', 'medium', 'high'] },
        due_date: { type: 'string', description: 'Optional ISO date (YYYY-MM-DD)' },
        status: { type: 'string', enum: ['todo', 'in_progress', 'done', 'dismissed'], description: 'To do / In progress / Done / Dismissed. Sets done to match.' },
        subtasks: {
          type: 'array',
          description: 'Optional checklist inside the task, in order (at most 50, titles up to 200 characters). Subtasks do not close the task.',
          items: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              done: { type: 'boolean' },
            },
            required: ['title'],
          },
        },
      },
      required: ['board_id', 'title'],
    },
  },
  {
    name: 'update_task',
    description:
      'Update an existing task. Provide task_id and any of: title, description (replaces the whole description), priority, due_date, group_id (move to another list), status (todo, in_progress, done, dismissed), done (true to mark complete), subtasks (replaces the checklist). Set status in_progress when you start working on a task and done when you finish. To edit a description, start from the text get_board returned: keep each "🖼 Image: name" line as it is to keep that image (delete the line to remove it); a line naming an image the task does not have stays plain text.',
    input_schema: {
      type: 'object',
      properties: {
        task_id: { type: 'string' },
        board_id: { type: 'string', description: 'Optional board id hint if known' },
        title: { type: 'string' },
        description: { type: 'string', description: 'New full description (markdown by default). Image lines from get_board keep their images.' },
        format: { type: 'string', enum: ['markdown', 'html'], description: 'Format of description (default markdown)' },
        priority: { type: 'string', enum: ['low', 'medium', 'high'] },
        due_date: { type: 'string' },
        group_id: { type: 'string', description: 'Move the task to this list' },
        done: { type: 'boolean' },
        status: { type: 'string', enum: ['todo', 'in_progress', 'done', 'dismissed'], description: 'To do / In progress / Done / Dismissed. Sets done to match.' },
        subtasks: {
          type: 'array',
          description: 'Replaces the whole checklist. Send every subtask you want to keep, with the id get_board returned (omit id for a new one); a subtask left out is removed.',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', description: 'Existing subtask id from get_board' },
              title: { type: 'string' },
              done: { type: 'boolean' },
            },
            required: ['title'],
          },
        },
      },
      required: ['task_id'],
    },
  },
  {
    name: 'write_task_description',
    description:
      "Write or replace a task's rich description. Provide task_id and content (markdown preferred). Use for detailed plans, checklists, or documentation on a task. \"🖼 Image: name\" lines from get_board keep the task's images; deleting one removes that image.",
    input_schema: {
      type: 'object',
      properties: {
        task_id: { type: 'string' },
        content: { type: 'string' },
        format: { type: 'string', enum: ['markdown', 'html'] },
      },
      required: ['task_id', 'content'],
    },
  },
];
